import "./lightbox.css";

import React from "react";

import useDialog from "./useDialog";

const Arrow = ({ isNext }) => (
  <svg
    viewBox={"0 0 24 24"}
    aria-hidden={"true"}
  >
    <path d={isNext ? "M9 4l8 8-8 8" : "M15 4l-8 8 8 8"} />
  </svg>
);

//Full-screen photo viewer with previous / next, thumbnails, arrow keys and swiping.
//"index" is the photo to open on - null when closed
const Lightbox = ({ title, photos, index, onClose }) => {
  const [current, setCurrent] = React.useState(index || 0);
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const thumbsRef = React.useRef(null);
  const touchStart = React.useRef(null);
  const isOpen = index !== null && index !== undefined;
  const count = photos.length;

  React.useEffect(() => {
    if (isOpen) {
      setCurrent(index);
    }
  }, [isOpen, index]);

  useDialog(isOpen, onClose, dialogRef, closeRef);

  const go = React.useCallback((step) => setCurrent((c) => (c + step + count) % count), [count]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        go(1);
      }
      else if (e.key === "ArrowLeft") {
        go(-1);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, go]);

  //Keep the current thumbnail in view, and load the next / previous photos ahead of time
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const thumb = thumbsRef.current && thumbsRef.current.children[current];
    if (thumb) {
      thumb.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }
    [1, -1].forEach((step) => {
      const img = new Image();
      img.src = photos[(current + step + count) % count].src;
    });
  }, [isOpen, current, photos, count]);

  if (!isOpen) {
    return null;
  }

  const photo = photos[current];

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) {
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) > 50) {
      go(dx < 0 ? 1 : -1);
    }
  };

  return (
    <div
      ref={dialogRef}
      className={"lightbox"}
      role={"dialog"}
      aria-modal={"true"}
      aria-label={`${title} photos`}
      tabIndex={-1}
    >
      <div className={"lightbox-bar"}>
        <p className={"lightbox-title"}>
          {title}
          <span className={"lightbox-count"}>
            {current + 1} / {count}
          </span>
        </p>
        <button
          ref={closeRef}
          type={"button"}
          className={"lightbox-close"}
          aria-label={"Close"}
          onClick={onClose}
        >
          <svg
            viewBox={"0 0 24 24"}
            aria-hidden={"true"}
          >
            <path d={"M5 5l14 14M19 5L5 19"} />
          </svg>
        </button>
      </div>

      <div
        className={"lightbox-stage"}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          //Clicking the dark area around the photo closes the viewer
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <img
          key={photo.src}
          src={photo.src}
          alt={photo.alt}
          className={"lightbox-image"}
        />
        {count > 1 ? (
          <React.Fragment>
            <button
              type={"button"}
              className={"lightbox-arrow is-prev"}
              aria-label={"Previous photo"}
              onClick={() => go(-1)}
            >
              <Arrow />
            </button>
            <button
              type={"button"}
              className={"lightbox-arrow is-next"}
              aria-label={"Next photo"}
              onClick={() => go(1)}
            >
              <Arrow isNext />
            </button>
          </React.Fragment>
        ) : null}
      </div>

      {count > 1 ? (
        <ul
          ref={thumbsRef}
          className={"lightbox-thumbs"}
        >
          {photos.map((p, i) => (
            <li key={p.src}>
              <button
                type={"button"}
                className={`lightbox-thumb ${i === current ? "is-current" : ""}`}
                aria-label={`Photo ${i + 1}`}
                aria-current={i === current ? "true" : undefined}
                onClick={() => setCurrent(i)}
              >
                <img
                  src={p.md}
                  alt={""}
                  loading={"lazy"}
                  width={120}
                  height={120}
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default Lightbox;
